import { type Runtime } from "@observablehq/runtime";
import * as im from "immutable";

import { type Cell, type CellInfo } from "~/models/cell";
import { type SpecifierInfo } from "~/runtime/cell-compiler/analysis";

import { CellResultsObserver } from "./CellResultsObserver";
import { Variable } from "./Variable";
import { type ObservableModule, type ResultsObserver } from "./interfaces";

type Import = {
	module: ObservableModule;
	specifiers: SpecifierInfo[];
};

type ImportCanvasDefineFunction = Promise<(module: string, observer: string) => void>;

/* CellHandles represent the binding between the Observable Runtime, and the moment Cell. A CellHandle
 * contains the knowledge of a cells name binding (an Observable Variable) and any imports done by
 * the cell.
 */

export class CellHandle {
	public cell: Cell;
	private _disposed = false;

	constructor(
		public cellInfo: CellInfo,
		private resultsObserver: CellResultsObserver,
		private runtime: Runtime,
		private module: ObservableModule,
		public variable: Variable,
		public viewOfVariable: Variable,
		public _import: Promise<Import | undefined> | undefined,
		public importVariables: im.List<Variable>
	) {
		this.variable = variable;
		this.viewOfVariable = viewOfVariable;
		this._import = _import;
		this.importVariables = importVariables;
		this.cell = cellInfo.cell;
	}

	static fromCellInfo(cellInfo: CellInfo, runtime: Runtime, module: ObservableModule) {
		const resultsObserver = new CellResultsObserver();
		const cellHandle = new this(
			cellInfo,
			resultsObserver,
			runtime,
			module,
			new Variable(module, resultsObserver),
			new Variable(module),
			undefined,
			im.List()
		);

		void cellHandle.defineVariables();

		return cellHandle;
	}

	dispose() {
		this._disposed = true;
		this.variable.delete();
		this.viewOfVariable.delete();
		this.importVariables.forEach((ref) => ref.delete());
		this.resultsObserver.resetStaticError();
	}

	/* Warning! Only one subscriber is allowed at a time. Calling this function
	 * with a new subscriber will destory the previous binding.
	 */
	setSubscriber(observer: ResultsObserver) {
		this.resultsObserver.setSubscriber(observer);
	}

	updateCell(cellInfo: CellInfo): CellHandle {
		const cellHandle = new CellHandle(
			cellInfo,
			this.resultsObserver,
			this.runtime,
			this.module,
			this.variable,
			this.viewOfVariable,
			this._import,
			this.importVariables
		);

		this.dispose();

		void cellHandle.defineVariables(this);
		return cellHandle;
	}

	get currentResult() {
		return this.resultsObserver.currentResult;
	}

	private async makeImport(
		previousCell: CellHandle | undefined,
		specifiers: SpecifierInfo[],
		define: ImportCanvasDefineFunction
	): Promise<Import | undefined> {
		if (this._import !== undefined) {
			// IMPORTANT: We *MUST AWAIT* this promise and `Object.assign` the values inside into a
			// new object. If we `Object.assign` the promise itself, sometimes the values will be
			// missing for reasons we don't fully understand.
			const imp = await this._import;
			return Object.assign({}, imp, { specifiers });
		}

		if (previousCell === undefined || previousCell._import === undefined) {
			return {
				module: this.runtime.module(await define),
				specifiers,
			};
		}
	}

	private async makeImportVariables(specifiers: SpecifierInfo[]): Promise<Variable[]> {
		if (!this._import) {
			return [];
		}

		const imports = specifiers.map(async (spec) => {
			const variable = new Variable(this.module);

			//
			// IMPORTANT: PREVENTS "WALL OF ERRORS" PROBLEM! This line makes everything that
			// depends on the import render as a loading bar instead of showing errors of the
			// form "X variable is not defined".
			//
			// This works because `this._import` is a promise. The runtime passes promises
			// between cells as results of type `PENDING`, so the UI can render cells that
			// depend on unresolved promises as loading bars.
			//

			variable.define({
				name: spec.localIdentifier,
				// IMPORTANT: Return type here is future-proofing. This must be a promise
				// because while it is unresolved, it will cause cells that depend on it to be
				// loading bars instead of errors.
				definition: (): Promise<Import | undefined> | undefined => this._import,
			});

			//
			// Resolve the import.
			//

			const imp = await this._import;

			//
			// Dispose if we just woke up and had been disposed in the meantime.
			//

			if (this._disposed) {
				variable.delete();
				return variable;
			}

			//
			// Actually import the variables from the other module.
			//

			if (imp) {
				variable.delete();
				variable.createImport(spec.imported, spec.localIdentifier, imp.module);
			} else {
				console.warn("makeImportVariables: this._import is undefined");
			}

			return variable;
		});

		return Promise.all(imports);
	}

	private async setImports(
		previousCell: CellHandle | undefined,
		specifiers: SpecifierInfo[],
		define: ImportCanvasDefineFunction
	) {
		//
		// IMPORTANT: Do NOT `await this._import` here. It will cause all cells depending on an
		// imported variable to render as an error until the import is resolved.
		//

		this._import = this.makeImport(previousCell, specifiers, define);

		//
		// Define the imported variables. This must be done AFTER setting `this._import` because
		// they need to `await this._import` before importing any variables from the module.
		//

		this.importVariables = im.List(await this.makeImportVariables(specifiers));
	}

	private async defineVariables(previousCell?: CellHandle) {
		if (this.cellInfo.status !== "executable") {
			return;
		}
		const emitInfo = this.cellInfo.emitInfo;
		switch (emitInfo.kind) {
			case "CanvasImport": {
				try {
					await this.setImports(
						previousCell,
						Object.values(emitInfo.specifiers),
						Function(`return ${emitInfo.emittedDefineFunction}`)()
					);
				} catch (e) {
					this.variable.define({
						definition: () => {
							throw e;
						},
					});
				}

				return;
			}
			case "PageImport": {
				try {
					await this.setImports(
						previousCell,
						Object.values(emitInfo.specifiers),
						Function(`return ${emitInfo.emittedDefineFunction}`)()
					);
				} catch (e) {
					this.variable.define({
						definition: () => {
							throw e;
						},
					});
				}

				return;
			}

			case "VariableDefinition": {
				try {
					const refs = emitInfo.refs || [];
					const f = Function(`return ${emitInfo.emittedDefineFunction}`)();
					if (emitInfo.id === undefined || emitInfo.id === null || emitInfo.id === "") {
						// gives a definition to the variable for this cell
						// https://github.com/observablehq/runtime#module_define
						this.variable.define({
							inputs: refs,
							definition: f,
						});
					} else {
						this.variable.define({
							name: emitInfo.id,
							inputs: refs,
							definition: f,
						});
					}
				} catch (e) {
					this.variable.define({
						definition: () => {
							throw e;
						},
					});
				}

				return;
			}

			case "ViewVariableDefinition": {
				try {
					const newDefinitionId = `viewof ${emitInfo.id}`;
					const refs = emitInfo.refs || [];
					const f = Function(`return ${emitInfo.emittedDefineFunction}`)();

					this.variable.define({
						name: newDefinitionId,
						inputs: refs,
						definition: f,
					});

					const newViewofDefinitionId = emitInfo.id;
					this.viewOfVariable.define({
						name: newViewofDefinitionId,
						inputs: ["Generators", newDefinitionId],
						definition: (generators: unknown, view: unknown) =>
							(generators as Generators)?.input?.(view),
					});
				} catch (e) {
					this.variable.define({
						definition: () => {
							throw e;
						},
					});
				}

				return;
			}
		}
	}
}
